var CollectionRecommendations = class extends HTMLElement {
  constructor() {
    super();
    this.isLoaded = false;
    this.observer = null;
    this.collectionObserver = null;
    this.facetObserver = null;
    this.limit = 12;
    this.seedHandles = new Set();
    this.recommendationCards = [];
    this.resultTemplate = null;
    this.renderedRecommendationSignature = null;
    this.loadAbortController = null;
    this.sectionSpacingOverride = null;
    this.handleFacetUpdate = this.handleFacetUpdate.bind(this);
  }

  connectedCallback() {
    if (this.hasAttribute('data-remove-section')) {
      this.removeSection();
      return;
    }

    if (this.isLoaded && this.resultTemplate) {
      this.observeCollectionProducts();
      document.addEventListener('facet:update', this.handleFacetUpdate);
      this.renderAvailableRecommendations();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.loadRecommendations();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.loadRecommendations();
        }
      },
      { rootMargin: '800px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.loadAbortController) {
      this.loadAbortController.abort();
      this.loadAbortController = null;
      this.isLoaded = false;
    }

    this.observer?.disconnect();
    this.collectionObserver?.disconnect();
    this.facetObserver?.disconnect();
    this.clearSectionSpacingOverride();
    document.removeEventListener('facet:update', this.handleFacetUpdate);
  }

  async loadRecommendations() {
    if (this.isLoaded) {
      return;
    }

    this.isLoaded = true;
    const loadAbortController = new AbortController();
    this.loadAbortController = loadAbortController;

    const seedProducts = [...new Set((this.dataset.seedProducts || '').split(',').filter(Boolean))];
    this.seedHandles = new Set((this.dataset.seedHandles || '').split(',').filter(Boolean));
    const section = this.closest('.shopify-section');

    if (!section || seedProducts.length === 0) {
      this.removeSection();
      return;
    }

    this.limit = Math.max(2, Math.min(Number.parseInt(this.dataset.limit, 10) || 12, 12));
    const fetchLimit = Math.min(this.limit, 10);
    const results = [];

    for (let index = 0; index < seedProducts.length; index += 4) {
      if (loadAbortController.signal.aborted || !this.isConnected) {
        return;
      }

      const batch = await Promise.all(
        seedProducts
          .slice(index, index + 4)
          .map((productId) => this.fetchRecommendations(productId, fetchLimit, section.id, loadAbortController.signal))
      );

      if (loadAbortController.signal.aborted || !this.isConnected) {
        return;
      }

      results.push(...batch.filter(Boolean));
    }

    this.loadAbortController = null;

    const sourceResult = results.find((result) => result.container);
    const cards = this.consolidateCards(results, this.seedHandles);

    if (!sourceResult || cards.length === 0) {
      this.removeSection();
      return;
    }

    const container = document.importNode(sourceResult.container, true);
    const productList = container.querySelector('product-list');
    const carousel = container.querySelector('scroll-carousel');

    if (!productList || !carousel) {
      this.removeSection();
      return;
    }

    productList.replaceChildren();
    this.resultTemplate = container;
    this.recommendationCards = cards.map((card) => document.importNode(card, true));
    this.renderAvailableRecommendations();
    this.observeCollectionProducts();
    document.addEventListener('facet:update', this.handleFacetUpdate);
    this.removeAttribute('aria-busy');
  }

  getDisplayedCollectionHandles(seedHandles) {
    const excludedHandles = new Set(seedHandles);

    document
      .querySelectorAll('product-list[data-navigator-section^="products-included-"] product-card[data-product-handle]')
      .forEach((card) => excludedHandles.add(card.dataset.productHandle));

    return excludedHandles;
  }

  observeCollectionProducts() {
    const productList = document.querySelector('product-list[data-navigator-section^="products-included-"]');
    this.collectionObserver?.disconnect();

    if (!productList || !('MutationObserver' in window)) {
      return;
    }

    this.collectionObserver = new MutationObserver(() => this.renderAvailableRecommendations());
    this.collectionObserver.observe(productList, { childList: true, subtree: true });
  }

  handleFacetUpdate(event) {
    const sectionId = event.detail?.url?.searchParams.get('section_id');
    const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;

    if (!section?.classList.contains('shopify-section--main-collection')) {
      return;
    }

    this.facetObserver?.disconnect();
    this.facetObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.target === section)) {
        return;
      }

      this.facetObserver.disconnect();
      this.observeCollectionProducts();
      this.renderAvailableRecommendations();
    });
    this.facetObserver.observe(section, { childList: true, subtree: true });
  }

  renderAvailableRecommendations() {
    const excludedHandles = this.getDisplayedCollectionHandles(this.seedHandles);
    const cards = this.recommendationCards
      .filter((card) => !excludedHandles.has(card.dataset.productHandle || card.getAttribute('handle')))
      .slice(0, this.limit);
    const signature = cards.map((card) => card.dataset.productHandle || card.getAttribute('handle')).join(',');

    if (signature === this.renderedRecommendationSignature) {
      return;
    }

    this.renderedRecommendationSignature = signature;

    if (cards.length === 0) {
      this.setSectionVisibility(false);
      this.replaceChildren();
      return;
    }

    const container = this.resultTemplate?.cloneNode(true);
    const productList = container?.querySelector('product-list');
    const carousel = container?.querySelector('scroll-carousel');

    if (!container || !productList || !carousel) {
      return;
    }

    const renderedCards = cards.map((card) => card.cloneNode(true));
    renderedCards.forEach((card) => card.classList.remove('is-selected'));
    productList.replaceChildren(...renderedCards);
    productList.dataset.navigatorTotalCount = String(renderedCards.length);
    container.querySelector('[is="prev-button"]')?.setAttribute('disabled', '');
    container.querySelector('[is="next-button"]')?.removeAttribute('disabled');
    this.replaceChildren(container);
    this.setSectionVisibility(true);
    this.syncCarouselState(productList, carousel, container.querySelector('scroll-progress'));
  }

  setSectionVisibility(isVisible) {
    const section = this.closest('.shopify-section');
    this.clearSectionSpacingOverride();
    this.hidden = !isVisible;

    if (!section) {
      return;
    }

    section.hidden = !isVisible;

    if (isVisible || !section.nextElementSibling) {
      return;
    }

    let previousSection = section.previousElementSibling;

    while (previousSection?.hidden) {
      previousSection = previousSection.previousElementSibling;
    }

    const nextSection = section.nextElementSibling;
    const previousHash = previousSection
      ? getComputedStyle(previousSection).getPropertyValue('--section-background-hash').trim() || '0'
      : '0';

    this.sectionSpacingOverride = {
      element: nextSection,
      value: nextSection.style.getPropertyValue('--previous-section-background-hash'),
      priority: nextSection.style.getPropertyPriority('--previous-section-background-hash')
    };
    nextSection.style.setProperty('--previous-section-background-hash', previousHash);
  }

  clearSectionSpacingOverride() {
    if (!this.sectionSpacingOverride) {
      return;
    }

    const { element, value, priority } = this.sectionSpacingOverride;

    if (value) {
      element.style.setProperty('--previous-section-background-hash', value, priority);
    } else {
      element.style.removeProperty('--previous-section-background-hash');
    }

    this.sectionSpacingOverride = null;
  }

  syncCarouselState(productList, carousel, scrollProgress) {
    if (!productList || !carousel) {
      return;
    }

    requestAnimationFrame(() => {
      if (!carousel.isConnected) {
        return;
      }

      const isScrollable = carousel.scrollWidth - carousel.clientWidth > 1;
      const progress = carousel.scrollWidth > 0 ? carousel.clientWidth / carousel.scrollWidth : 1;

      carousel.classList.toggle('is-scrollable', isScrollable);
      scrollProgress?.style.setProperty('--scroll-progress', Math.max(0, Math.min(progress, 1)).toFixed(6));
    });
  }

  async fetchRecommendations(productId, limit, sectionId, signal) {
    const url = new URL(`${Shopify.routes.root}recommendations/products`, window.location.origin);
    url.search = new URLSearchParams({
      product_id: productId,
      limit: String(limit),
      section_id: sectionId.replace('shopify-section-', ''),
      intent: 'related'
    });

    try {
      const response = await fetch(url, { priority: 'low', signal });

      if (!response.ok) {
        return null;
      }

      const documentResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
      const container = documentResponse.querySelector('[data-collection-recommendation-results]');
      const sectionStack = container?.querySelector('.section-stack');
      const cards = [...(container?.querySelectorAll('product-card') || [])];

      return container && sectionStack && cards.length > 0 ? { container, cards } : null;
    } catch {
      return null;
    }
  }

  consolidateCards(results, excludedHandles) {
    const rankedCards = new Map();
    let firstSeen = 0;

    results.forEach((result) => {
      result.cards.forEach((card, index) => {
        const handle = card.dataset.productHandle || card.getAttribute('handle');

        if (!handle || excludedHandles.has(handle)) {
          return;
        }

        const rank = result.cards.length - index;
        const existing = rankedCards.get(handle);

        if (existing) {
          existing.matches += 1;
          existing.rank += rank;
        } else {
          rankedCards.set(handle, { card, matches: 1, rank, firstSeen: firstSeen++ });
        }
      });
    });

    return [...rankedCards.values()]
      .sort((left, right) => right.matches - left.matches || right.rank - left.rank || left.firstSeen - right.firstSeen)
      .map(({ card }) => card);
  }

  removeSection() {
    this.closest('.shopify-section')?.remove();
  }
};

if (!window.customElements.get('collection-recommendations')) {
  window.customElements.define('collection-recommendations', CollectionRecommendations);
}
